import { Routes } from '../../app/routes';
import type { AutonomyKind, Employee, Item, KindAutonomy, RequestStatus, Workspace } from '../../domain/models';
import { allAllowedAutomatic, automaticKinds } from '../../domain/models';
import type { Translate } from '../../l10n/i18n';
import { timeAgo, type Tone } from '../core/presentation';
import { foldByKind, type WorkEntry } from '../inbox/workGroups';

// What the team screen and an employee's page show, ported from team_view_model.dart and
// team_screen.dart.

/** The workspace the address asks for (`?workspace=slug`, or its id), or the first one. */
export function selectedWorkspace(workspaces: Workspace[] | undefined, wanted: string | null): Workspace | undefined {
  return workspaces?.find((w) => w.slug === wanted || w.id === wanted) ?? workspaces?.[0];
}

/** Where the team page opens on a workspace. */
export const teamHref = (slugOrId?: string) =>
  slugOrId ? `${Routes.team}?workspace=${encodeURIComponent(slugOrId)}` : Routes.team;

/** A color, faint, for tints behind an employee's things. */
export const tint = (color: string, percent = 14) => `color-mix(in srgb, ${color} ${percent}%, transparent)`;

/** Only employees who take tasks really "work"; for the rest approved items just finish. */
export const isWorkingNow = (e: Employee) => e.active && e.takesTasks && e.inProgressCount > 0;

/** One line on how an employee is doing: paused, working, waiting on the person, or when they last
 * worked; with the color of the dot before it. */
export function employeeState(t: Translate, e: Employee, now = new Date()): { label: string; dot: string; pulse: boolean } {
  if (!e.active) return { label: t('teamPaused'), dot: 'var(--ink-faint)', pulse: false };
  if (isWorkingNow(e)) return { label: t('teamWorking', { count: e.inProgressCount }), dot: 'var(--accent)', pulse: true };
  if (e.openCount > 0) return { label: t('teamOpen', { count: e.openCount }), dot: 'var(--accent)', pulse: false };
  if (!e.lastActivityAt) return { label: t('teamNoWorkYet'), dot: 'var(--success)', pulse: false };
  return { label: t('teamLastWork', { when: timeAgo(t, e.lastActivityAt, now) }), dot: 'var(--success)', pulse: false };
}

/** Who last changed an employee's autonomy, and when: "Paskutinį kartą keitė Povilas, prieš 2 val.". */
export function autonomyChange(t: Translate, e: Employee, now = new Date()): string | undefined {
  if (!e.autonomyChangedBy || !e.autonomyChangedAt) return undefined;
  return t('autonomyChangedOn', { name: e.autonomyChangedBy, when: timeAgo(t, e.autonomyChangedAt, now) });
}

/** The team card's word on autonomy: all of the work that may be automatic is, some is, or nothing. */
export function autonomyBadge(t: Translate, e: Pick<Employee, 'autonomy'>): string | undefined {
  if (allAllowedAutomatic(e)) return t('autonomyBadgeAll');
  if (automaticKinds(e).length > 0) return t('autonomyBadgeSome');
  return undefined;
}

/** The kinds an employee's autonomy offers a switch for: what it carries out itself (the server lists
 * only those, ADR 0009). */
export const switchableKinds = (e: Pick<Employee, 'autonomy'>) => e.autonomy.filter((k) => k.allowed);

/** What automatic means for a kind, where it is not plain: requests are let in after the check. */
export function autonomyNote(t: Translate, k: KindAutonomy): string | undefined {
  return k.kind === 'request' ? t('autonomyRequestHint') : undefined;
}

/** The question before a kind goes automatic: its work then goes ahead unseen. Requests have their own,
 * about the isolated check (ADR 0011). */
export function autonomyConfirm(t: Translate, kind: AutonomyKind, name: string): { title: string; body: string } {
  if (kind === 'request') return { title: t('autonomyConfirmRequestTitle'), body: t('autonomyConfirmRequestBody') };
  return { title: t('autonomyConfirmTitle', { kind: name }), body: t('autonomyConfirmBody') };
}

/** A request's state in words, with the tone of its pill; `working` while the team sets it up. */
export function requestStatus(t: Translate, status: RequestStatus): { label: string; tone: Tone; working: boolean } {
  switch (status) {
    case 'requested':
      return { label: t('requestStatusRequested'), tone: 'muted', working: false };
    case 'in_progress':
      return { label: t('requestStatusInProgress'), tone: 'accent', working: true };
    case 'done':
      return { label: t('requestStatusDone'), tone: 'success', working: false };
    case 'declined':
      return { label: t('requestStatusDeclined'), tone: 'muted', working: false };
    default:
      return { label: '?', tone: 'muted', working: false };
  }
}

export type EmployeeTab = 'waiting' | 'tasks' | 'all';

/** Only employees who take tasks have a tab for them. */
export const employeeTabs = (e?: Pick<Employee, 'takesTasks'>): EmployeeTab[] =>
  e?.takesTasks ? ['waiting', 'tasks', 'all'] : ['waiting', 'all'];

/** What waits is folded by kind, as in the inbox; other tabs list every item. */
export const tabEntries = (tab: EmployeeTab, items: Item[]): WorkEntry[] =>
  tab === 'waiting' ? foldByKind(items) : items.map((item) => ({ type: 'single', item }));
